import { StoreError } from "../../errors";
import { AnyAction } from "redux";
import {
  IdResourceState,
  buildIdResourceActionTypes,
  buildIdResourceActions,
  buildIdResourceReducer,
  buildIdResourceSelectors,
  initialIdResourceState,
} from "../../util/id-resource";
import {
  ACTION_USERS_LIST_LOADED,
  ACTION_USERS_LIST_LOADING,
  ACTION_USERS_LIST_ERRORED,
} from "./load";
import { ACTION_USERS_LISTEN_CHANGE } from "./listen-change";

export type User = {
  id: string;
  email: string;
  displayName?: string | null;
  hasChampionBelt?: boolean;
};

/**
 * The users slice loads "the related users for event X" — keyed by event id.
 * Each value is the User[] for that event. Per-event request tokens prevent a
 * slow A → B → A switch from leaving stale users on screen (the source of
 * the Stats / Tasters loading bug).
 *
 * Legacy fields (`items`, `loading`, `loadedFor`, `error`) are mirrored from
 * the latest committed load so existing screens keep working without touching
 * their selectors in this PR.
 */
export type UsersState = IdResourceState<User[]> & {
  // Legacy mirrors (= the latest committed event's data):
  loading: boolean;
  itemsLoading: { [key: string]: boolean };
  itemsError: { [key: string]: StoreError };
  loaded: boolean;
  loadedFor: string | null;
  items: User[] | null;
  error: StoreError | null;
};

export const USERS_ACTION_TYPES = buildIdResourceActionTypes("users-list");
export const usersActions = buildIdResourceActions<User[]>(USERS_ACTION_TYPES);
/** Selectors for "users for event X". Returns the User[] or undefined. */
export const usersSelectors = buildIdResourceSelectors<
  { users: UsersState },
  User[]
>((root) => root.users);

const idResourceReducer = buildIdResourceReducer<User[], UsersState>(
  USERS_ACTION_TYPES
);

const initialState: UsersState = {
  ...initialIdResourceState<User[]>(),
  loading: false,
  itemsLoading: {},
  itemsError: {},
  loaded: false,
  loadedFor: null,
  items: null,
  error: null,
};

const sortUsers = (i1: User, i2: User): number => {
  return -1 * (i2?.displayName?.localeCompare(i1?.displayName ?? "") ?? 0);
};

/** Walk every event's user list and update one user wherever they appear.
 *  A single user (e.g. their displayName changed) may be in multiple lists. */
const updateUserAcrossLists = (
  itemsById: Record<string, User[]>,
  user: User
): Record<string, User[]> => {
  const next: Record<string, User[]> = {};
  for (const [eventId, list] of Object.entries(itemsById)) {
    let touched = false;
    const updated = list.map((u) => {
      if (u.id === user.id) {
        touched = true;
        return { ...u, ...user };
      }
      return u;
    });
    next[eventId] = touched ? updated.sort(sortUsers) : list;
  }
  return next;
};

const ITEMSBYID_TOUCHING_ACTIONS = new Set<string>([
  USERS_ACTION_TYPES.startedLoading,
  USERS_ACTION_TYPES.loaded,
  USERS_ACTION_TYPES.errored,
  USERS_ACTION_TYPES.removed,
  ACTION_USERS_LIST_LOADED,
  ACTION_USERS_LIST_LOADING,
  ACTION_USERS_LIST_ERRORED,
  ACTION_USERS_LISTEN_CHANGE,
]);

const reflectLegacyMirrors = (state: UsersState): UsersState => {
  const itemsById = state.itemsById ?? {};
  const ids = Object.keys(itemsById);
  // The "current" event id is whatever's been committed most recently. The
  // load thunk sets `loadedFor` directly on commit; for stale state we fall
  // back to whatever's in itemsById.
  const currentId = state.loadedFor ?? ids[ids.length - 1] ?? null;
  const items = currentId ? itemsById[currentId] ?? null : null;
  const anyLoading = Object.values(state.loadingFor ?? {}).some(Boolean);
  return {
    ...state,
    items: items ?? state.items,
    loadedFor: currentId ?? state.loadedFor,
    loading: anyLoading,
    error: currentId ? state.errorFor?.[currentId] ?? null : state.error,
    itemsLoading: state.loadingFor ?? state.itemsLoading ?? {},
    itemsError: state.errorFor
      ? (Object.fromEntries(
          Object.entries(state.errorFor).filter(([, e]) => e !== null)
        ) as { [key: string]: StoreError })
      : state.itemsError ?? {},
  };
};

export default function usersReducer(
  state: UsersState = initialState,
  action: AnyAction
): UsersState {
  let next = idResourceReducer(state, action);

  switch (action.type) {
    case ACTION_USERS_LIST_LOADING: {
      const { id, requestToken } = action.payload as {
        id: string;
        requestToken: number;
      };
      next = {
        ...next,
        loadingFor: { ...next.loadingFor, [id]: true },
        errorFor: { ...next.errorFor, [id]: null },
        requestToken: { ...next.requestToken, [id]: requestToken },
      };
      break;
    }
    case ACTION_USERS_LIST_LOADED: {
      const { id, items, requestToken } = action.payload as {
        id: string;
        items: User[];
        requestToken: number;
      };
      if ((state.requestToken?.[id] ?? 0) !== requestToken) {
        // Stale: a newer fetch superseded this one.
        return next;
      }
      const sorted = [...items].sort(sortUsers);
      next = {
        ...next,
        itemsById: { ...next.itemsById, [id]: sorted },
        loadingFor: { ...next.loadingFor, [id]: false },
        errorFor: { ...next.errorFor, [id]: null },
        loaded: true,
        loadedFor: id,
      };
      break;
    }
    case ACTION_USERS_LIST_ERRORED: {
      const { id, error, requestToken } = action.payload as {
        id: string;
        error: StoreError;
        requestToken: number;
      };
      if ((state.requestToken?.[id] ?? 0) !== requestToken) return next;
      next = {
        ...next,
        loadingFor: { ...next.loadingFor, [id]: false },
        errorFor: { ...next.errorFor, [id]: error },
      };
      break;
    }
    case ACTION_USERS_LISTEN_CHANGE: {
      next = {
        ...next,
        itemsById: updateUserAcrossLists(next.itemsById ?? {}, action.payload as User),
      };
      break;
    }
    default:
      break;
  }

  if (ITEMSBYID_TOUCHING_ACTIONS.has(action.type)) {
    return reflectLegacyMirrors(next);
  }
  return next;
}
